import { useEffect, useState } from "react";
import api from "@/lib/api";

const EMPTY_FORM = {
  claim_id: null,
  type: "certificate",
  title: "",
  issuer: "",
  description: "",
  issued_at: "",
  expires_at: "",
  company_name: "",
  cvr_number: "",
  company_role: "",
  authorization_number: "",
  evidence_file_id: "",
};

const TYPE_LABELS = {
  education: "Uddannelse",
  certificate: "Certifikat",
  authorization: "Autorisation",
  affiliation: "Virksomhedstilknytning",
  other: "Anden kvalifikation",
};

export default function ProfessionalQualifications() {
  const [claims, setClaims] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/experts/me/professional-badges")
    .then((response) => setClaims(response.data || []))
    .catch(() => {});

  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const reset = () => {
    setForm(EMPTY_FORM);
    setFile(null);
    setOpen(false);
    setError("");
  };

  const editRejected = (claim) => {
    setForm({
      claim_id: claim.id,
      type: claim.type || claim.badge_type || "other",
      title: claim.title || claim.label || "",
      issuer: claim.issuer || "",
      description: claim.description || "",
      issued_at: claim.issued_at?.slice(0, 10) || "",
      expires_at: claim.expires_at?.slice(0, 10) || "",
      company_name: claim.company_name || "",
      cvr_number: claim.cvr_number || "",
      company_role: claim.company_role || "",
      authorization_number: claim.authorization_number || "",
      evidence_file_id: claim.evidence_file_id || "",
    });
    setFile(null);
    setOpen(true);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      let evidenceFileId = form.evidence_file_id;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/experts/me/qualification-evidence", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        evidenceFileId = data.evidence_file_id;
      }
      if (!evidenceFileId) throw new Error("Tilføj dokumentation");
      await api.post("/experts/me/professional-badges", {
        ...form,
        claim_id: form.claim_id || null,
        description: form.description || null,
        issued_at: form.issued_at || null,
        expires_at: form.expires_at || null,
        issuer: form.issuer || null,
        company_name: form.company_name || null,
        cvr_number: form.cvr_number || null,
        company_role: form.company_role || null,
        authorization_number: form.authorization_number || null,
        evidence_file_id: evidenceFileId,
      });
      await load();
      reset();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || "Kvalifikationen kunne ikke indsendes");
    } finally {
      setBusy(false);
    }
  };

  const viewEvidence = async (claim) => {
    try {
      const response = await api.get(`/qualification-evidence/${claim.evidence_file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError("Dokumentationen kunne ikke åbnes");
    }
  };

  return (
    <section className="card-outline p-5" data-testid="professional-qualifications">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-[15px] font-semibold text-brand-ink">Kvalifikationer & virksomhed</h3>
          <p className="mt-1 text-xs text-brand-muted">Indsend uddannelse, certifikater, autorisationer eller virksomhedstilknytning. Kategorier er ikke verificering. Kun konkret verificerede oplysninger vises offentligt.</p>
        </div>
        <button type="button" onClick={() => open ? reset() : setOpen(true)} className="chip" data-testid="add-qualification">
          {open ? "Annuller" : "Tilføj kvalifikation"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="qualification-form">
          <label className="text-sm">Type<select value={form.type} onChange={(event) => update("type", event.target.value)} className="mt-1 w-full card-outline bg-white px-3 py-2.5">{Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label className="text-sm">Titel<input required value={form.title} onChange={(event) => update("title", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Fx Azure Administrator Associate"/></label>
          <label className="text-sm">Udsteder {form.type === "affiliation" && <span className="text-brand-muted">(valgfrit)</span>}<input required={form.type !== "affiliation"} value={form.issuer} onChange={(event) => update("issuer", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Fx Microsoft eller Sikkerhedsstyrelsen"/></label>
          {(form.type === "affiliation" || form.type === "authorization") && <>
            <label className="text-sm">Virksomhedsnavn {form.type === "authorization" && <span className="text-brand-muted">(valgfrit)</span>}<input required={form.type === "affiliation"} value={form.company_name} onChange={(event) => update("company_name", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Fx ABC El ApS"/></label>
            <label className="text-sm">CVR-nummer <span className="text-brand-muted">(valgfrit)</span><input inputMode="numeric" pattern="[0-9]{8}" value={form.cvr_number} onChange={(event) => update("cvr_number", event.target.value.replace(/\D/g, "").slice(0, 8))} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="12345678"/></label>
            <label className="text-sm">Rolle/titel i virksomheden <span className="text-brand-muted">(valgfrit)</span><input value={form.company_role} onChange={(event) => update("company_role", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Fx Medejer / elektriker"/></label>
          </>}
          {form.type === "authorization" && <label className="text-sm">Autorisationsnummer<input required value={form.authorization_number} onChange={(event) => update("authorization_number", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Aut.nr."/></label>}
          <label className="text-sm">Udstedt dato <span className="text-brand-muted">(valgfrit)</span><input type="date" value={form.issued_at} onChange={(event) => update("issued_at", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5"/></label>
          <label className="text-sm">Udløbsdato <span className="text-brand-muted">(valgfrit)</span><input type="date" value={form.expires_at} onChange={(event) => update("expires_at", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5"/></label>
          <label className="text-sm">Dokumentation<input required={!form.evidence_file_id} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp" onChange={(event) => setFile(event.target.files?.[0] || null)} className="mt-1 block w-full text-xs"/><span className="mt-1 block text-[11px] text-brand-muted">PDF, JPG, PNG eller WebP. Maks. 10 MB. Dokumentet er privat.</span></label>
          <label className="sm:col-span-2 text-sm">Beskrivelse <span className="text-brand-muted">(valgfrit)</span><textarea rows={3} value={form.description} onChange={(event) => update("description", event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5"/></label>
          {error && <div className="sm:col-span-2 text-sm text-[#991B1B]" role="alert">{error}</div>}
          <button disabled={busy} className="btn-primary sm:col-span-2 sm:w-fit disabled:opacity-50">{busy ? "Indsender…" : form.claim_id ? "Ret og genindsend" : "Indsend til verificering"}</button>
        </form>
      )}

      <div className="mt-4 space-y-2">
        {claims.map((claim) => {
          const expired = claim.status === "verified" && claim.expires_at && new Date(claim.expires_at) <= new Date();
          const status = expired ? "Udløbet" : claim.status === "verified" ? "Verificeret af SpørgMig" : claim.status === "rejected" ? "Afvist" : "Afventer verificering";
          return <div key={claim.id} className="rounded-xl border border-brand-line p-3 text-sm"><div className="flex items-start justify-between gap-3 flex-wrap"><div><div className="font-medium text-brand-ink">{claim.title || claim.label}</div><div className="text-xs text-brand-muted">{TYPE_LABELS[claim.type || claim.badge_type] || "Kvalifikation"}{claim.issuer ? ` · ${claim.issuer}` : ""}</div>{claim.company_name && <div className="mt-1 text-xs text-brand-body">{claim.company_name}{claim.cvr_number ? ` · CVR ${claim.cvr_number}` : ""}{claim.company_role ? ` · ${claim.company_role}` : ""}</div>}{claim.authorization_number && <div className="mt-1 text-xs text-brand-body">Aut.nr.: {claim.authorization_number}</div>}<div className={`mt-1 text-xs ${claim.status === "verified" && !expired ? "text-brand-fresh" : claim.status === "rejected" ? "text-[#991B1B]" : "text-brand-muted"}`}>{status}</div>{claim.review_note && <div className="mt-1 text-xs text-brand-body">Feedback: {claim.review_note}</div>}</div><div className="flex gap-2">{claim.evidence_file_id && <button type="button" onClick={() => viewEvidence(claim)} className="text-xs link-underline">Se dokument</button>}{claim.status === "rejected" && <button type="button" onClick={() => editRejected(claim)} className="text-xs link-underline">Ret og genindsend</button>}</div></div></div>;
        })}
        {claims.length === 0 && <div className="text-sm text-brand-muted">Ingen kvalifikationer indsendt endnu.</div>}
      </div>
    </section>
  );
}

